import React, { useMemo, useState, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Typography, Button, CircularProgress, Alert, Chip, Snackbar, Dialog } from '@mui/material';
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import FolderOpenOutlined from '@mui/icons-material/FolderOpenOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { Instagram, YouTube, Twitter, Facebook, LinkedIn } from '@mui/icons-material';

import DashboardHeaderSection from '../common/dashboardheader/DashboardHeader';
import PublicReportHeader from './PublicReportHeader';
import { useFetchReportGroupSummary, useFetchReportGroupSnapshots, useDetachReportFromGroup } from '../../react-query/hooks/useReportGroupSummary';
// Reuse the same influencer-row / proxy-image / number-format / public-view-detection
// helpers CampaignReportDashboard uses for a single report, so a group's shareable
// link behaves identically to a single report's — same "no auth cookie = public" rule.
import CampaignReportDashboard, { mapInfluencerForTable, proxyImgUrl, fmt, checkIsPublicView } from './CampaignReportDashboard';
import { estimateReach } from './estimateReach';
import CreateReportDialog from './components/CreateReportDialog';

const DashboardTab   = lazy(() => import('./components/DashboardTab'));
const InfluencersTab = lazy(() => import('./components/InfluencersTab'));
const ContentsTab    = lazy(() => import('./components/ContentsTab'));

// Overview's own Dashboard/Influencers/Contents sub-tabs — same three tabs and
// icons CampaignReportDashboard.jsx uses for a single report (TAB_CONFIG/TabBtn),
// styled identically so switching between a group's Overview and any of its
// child-report tabs feels like the same surface.
const OVERVIEW_SUB_TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: PieChartOutlineOutlinedIcon },
    { id: 'influencers', label: 'Influencers', icon: PersonSearchOutlinedIcon },
    { id: 'contents', label: 'Contents', icon: CollectionsOutlinedIcon },
];

const OverviewSubTabs = ({ activeId, onChange }) => (
    <Box sx={{
        display: 'flex', gap: 0.5, bgcolor: '#fff', border: '1px solid #E8E9ED',
        borderRadius: 2, p: 0.75, mb: 3, width: 'fit-content', flexWrap: 'wrap',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    }}>
        {OVERVIEW_SUB_TABS.map(({ id, label, icon: Icon }) => {
            const isActive = id === activeId;
            return (
                <Box
                    key={id}
                    onClick={() => onChange(id)}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75 },
                        px: { xs: 1.5, sm: 2.5 }, py: { xs: 0.85, sm: 1.1 },
                        borderRadius: 1.75, cursor: 'pointer', transition: 'all 0.18s ease',
                        background: isActive ? 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)' : 'transparent',
                        boxShadow: isActive ? '0 4px 18px rgba(255,82,0,0.35)' : 'none',
                        '&:hover': { background: isActive ? 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)' : 'rgba(0,0,0,0.05)' },
                    }}
                >
                    <Icon sx={{ fontSize: { xs: 14, sm: 15 }, color: isActive ? '#fff' : '#52514E' }} />
                    <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: isActive ? '#fff' : '#52514E', whiteSpace: 'nowrap' }}>
                        {label}
                    </Typography>
                </Box>
            );
        })}
    </Box>
);

// Same platform-icon convention used in Reports.jsx / PlanSummary.jsx's ListTabs.
const PLATFORM_ICONS = {
    instagram: Instagram,
    youtube: YouTube,
    twitter: Twitter,
    facebook: Facebook,
    linkedin: LinkedIn,
};

const C = {
    orange: '#FF5200',
    border: '#E8E9ED',
};

// Builds the same `stats` shape DashboardTab expects (see computeStats() in
// CampaignReportDashboard.jsx) out of the group summary's flat mergedTotals —
// which only carries {influencerCount, postCount, totalLikes, totalComments,
// totalViews, totalReshares, totalPayment}, so fields a single report can compute
// from its raw posts (saves, reposts, plays, paid-partnership counts, snapshots…)
// simply aren't available at the group level and default to 0 / NA here.
function buildOverviewStats(mergedTotals) {
    const totalLikes      = mergedTotals?.totalLikes || 0;
    const totalComments   = mergedTotals?.totalComments || 0;
    const totalReshares   = mergedTotals?.totalReshares || 0;
    const totalViews      = mergedTotals?.totalViews || 0;
    const totalPayment    = Number(mergedTotals?.totalPayment) || 0;
    const totalEngagement = totalLikes + totalComments + totalReshares;
    const postCount       = mergedTotals?.postCount || 0;
    const engagementRate  = totalEngagement > 0 && totalViews > 0
        ? `${((totalEngagement / totalViews) * 100).toFixed(2)} %` : 'NA';
    const cpe = totalPayment > 0 && totalEngagement > 0 ? `₹${(totalPayment / totalEngagement).toFixed(2)}` : 'NA';
    const cpv = totalPayment > 0 && totalViews      > 0 ? `₹${(totalPayment / totalViews).toFixed(2)}`      : 'NA';

    return {
        isEmpty: postCount === 0,
        isProcessing: false,
        influencerCount: mergedTotals?.influencerCount || 0,
        liveInfluencerCount: mergedTotals?.influencerCount || 0,
        postsLive: postCount,
        total: postCount,
        campaignViews: fmt(totalViews),
        campaignViewsRaw: totalViews,
        estimatedReach: fmt(estimateReach(totalViews)),
        totalEngagement: fmt(totalEngagement),
        totalLikes, totalComments, totalReshares,
        totalSaves: 0, totalReposts: 0, totalViews, totalPlays: 0, totalFbPlays: 0,
        engagementRate, cpe, cpv,
        paidPartnershipCount: 0, hiddenLikesCount: 0,
    };
}

// Chip-style tab bar, matching PlanSummary.jsx's ListTabs (active = solid orange
// pill, inactive = outlined) — "Overview" is always first, followed by one tab
// per child report. Child-report tabs (not Overview) get MUI Chip's built-in
// delete affordance (the small "x"), same convention PlanSummary.jsx's own
// ListTabs uses for removing a list — wired to onRemoveTab instead of onChange.
const GroupTabs = ({ tabs, activeId, onChange, onRemoveTab, readOnly }) => (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {tabs.map((t) => {
            const isActive = t.id === activeId;
            const Icon = t.icon;
            const isOverview = t.id === 'overview';
            return (
                <Chip
                    key={t.id}
                    onClick={() => onChange(t.id)}
                    onDelete={(isOverview || readOnly) ? undefined : (e) => { e.stopPropagation(); onRemoveTab(t); }}
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {Icon && <Icon sx={{ fontSize: 14, color: isActive ? 'rgba(255,255,255,0.9)' : '#6B7280' }} />}
                            <span style={{ color: isActive ? '#fff' : '#111827', fontWeight: isActive ? 600 : 500 }}>
                                {t.label}
                            </span>
                        </Box>
                    }
                    sx={{
                        px: 0.5,
                        border: '1px solid',
                        borderColor: isActive ? C.orange : '#E5E7EB',
                        bgcolor: isActive ? C.orange : '#fff',
                        transition: 'all 0.15s ease',
                        '&:hover': { bgcolor: isActive ? C.orange : '#F9FAFB', opacity: isActive ? 0.92 : 1 },
                        '& .MuiChip-deleteIcon': { color: isActive ? 'rgba(255,255,255,0.75)' : '#9CA3AF' },
                        '& .MuiChip-deleteIcon:hover': { color: isActive ? '#fff' : '#EF4444' },
                    }}
                />
            );
        })}
    </Box>
);

const ReportGroupDashboard = () => {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [tab, setTab] = useState('overview');
    const [overviewSubTab, setOverviewSubTab] = useState('dashboard');
    const [addReportOpen, setAddReportOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [openAlert, setOpenAlert] = useState(false);

    // Same rule as a single report's shareable link: no valid auth cookie means
    // this is an anonymous visitor on the group's dashboard URL, so hide every
    // edit affordance (Add Report, detach) and swap in the slim public header.
    const isPublicView = useMemo(() => checkIsPublicView(), []);

    const { data: summaryResp, isLoading, isError } = useFetchReportGroupSummary(groupId);
    const summary = summaryResp?.data ?? null;

    // Merged trend line for the Overview's Dashboard sub-tab — same shape as a
    // single report's own snapshots array, see routes/reportGroups.js's
    // GET /:groupId/snapshots for how it's aggregated across the group's reports.
    const { data: groupSnapshotsResp } = useFetchReportGroupSnapshots(groupId);
    const groupSnapshots = groupSnapshotsResp?.data ?? [];

    // ── Remove a child report from this group ────────────────────────────────
    // "Delete" here only detaches — the report itself is never touched, it just
    // stops belonging to this group and shows up in the flat Reports list again
    // exactly as it did before being grouped.
    const detachMutation = useDetachReportFromGroup(groupId);
    const [pendingDetach, setPendingDetach] = useState(null);
    const [detachError, setDetachError] = useState(null);

    const handleRemoveTab = (t) => {
        setDetachError(null);
        setPendingDetach(t);
    };
    const handleCancelDetach = () => {
        if (detachMutation.isPending) return;
        setPendingDetach(null);
        setDetachError(null);
    };
    const handleConfirmDetach = async () => {
        if (!pendingDetach) return;
        setDetachError(null);
        try {
            await detachMutation.mutateAsync({ reportId: pendingDetach.id });
            // The removed report's own tab is gone once the summary refetches —
            // fall back to Overview if it was the one currently being viewed.
            if (tab === pendingDetach.id) setTab('overview');
            setPendingDetach(null);
            setSuccessMessage(`"${pendingDetach.label}" removed from this group — it's back in your normal Reports list.`);
            setOpenAlert(true);
        } catch (err) {
            setDetachError(err?.response?.data?.message || err?.response?.data || 'Failed to remove this report from the group. Please try again.');
        }
    };

    // Memoized so this array reference is stable across renders when summary.reports
    // is empty/undefined (an inline `?? []` fallback would otherwise be a new array
    // every render, making the `tabs` useMemo below recompute needlessly).
    const reports = useMemo(() => summary?.reports ?? [], [summary]);
    const hasReports = reports.length > 0;

    const overviewStats = useMemo(() => buildOverviewStats(summary?.mergedTotals), [summary]);

    // Shape mirrors CampaignReportDashboard's `enrichedReport` — only the fields
    // DashboardTab actually reads are populated; per-post derived sections
    // (topContents/topHashtags/topWords/sentiment/keywords/topEmojis) have no
    // group-level equivalent in the summary payload and are left undefined,
    // same as they'd be for any single report with no processed posts yet.
    const overviewReport = useMemo(() => {
        if (!summary) return null;
        const mergedInfluencers = summary.mergedInfluencers || [];
        const allInfluencers = mergedInfluencers
            .map((inf) => ({
                id: inf.username, handle: '@' + inf.username, name: inf.fullName || inf.username,
                followers: inf.followerCount || 0, posts: inf.postCount || 0,
                engagements: (inf.totalLikes || 0) + (inf.totalComments || 0) + (inf.totalReshares || 0),
                reelViews: inf.totalViews || 0, avatar: proxyImgUrl(inf.profilePicUrl), category: '',
            }))
            .sort((a, b) => b.engagements - a.engagements);
        return {
            _id: null,
            totalPayment: Number(summary.mergedTotals?.totalPayment) || 0,
            allInfluencers,
            topInfluencers: allInfluencers.slice(0, 12),
            topContents: undefined,
            topHashtags: undefined,
            topWords: undefined,
            keywords: undefined,
            topEmojis: undefined,
            sentiment: undefined,
            // No single report backs this merged view, so there's nothing for
            // "Load Comments" to fetch — marking it loaded hides that button
            // instead of leaving a dead click target.
            commentsLoaded: true,
        };
    }, [summary]);

    const mergedInfluencersMapped = useMemo(() => {
        const list = summary?.mergedInfluencers;
        if (!list || !list.length) return undefined;
        return list.map((inf) => mapInfluencerForTable(inf, {
            totalPayment: Number(summary?.mergedTotals?.totalPayment) || 0,
            influencerCount: list.length,
        }));
    }, [summary]);

    // ContentsTab.jsx only ever reads report.posts and report.campaign (to gate
    // delete) — `campaign: null` plus the `isPublic` prop below is enough to
    // render every child report's posts read-only in one merged grid.
    const mergedPostsReport = useMemo(() => ({
        posts: summary?.mergedPosts || [],
        campaign: null,
    }), [summary]);

    const tabs = useMemo(() => ([
        { id: 'overview', label: 'Overview', icon: PieChartOutlineOutlinedIcon },
        ...reports.map((r) => ({ id: r.id, label: r.name, icon: PLATFORM_ICONS[(r.platform || '').toLowerCase()] || null })),
    ]), [reports]);

    const handleAddReport = () => setAddReportOpen(true);
    const handleReportCreated = (newReport) => {
        // The new report isn't in `summary.reports` yet (that's driven by the
        // group-summary query) — refetch it, and jump straight to the new
        // report's tab once it lands instead of leaving the user on Overview.
        queryClient.invalidateQueries({ queryKey: ['reportGroupSummary', groupId] });
        setTab(String(newReport._id));
    };

    // Public view drops the app sidebar (80px) and uses the slim fixed 56px
    // header — same layout shift CampaignReportDashboard.jsx makes for a single
    // report's shareable link.
    const contentWidth = isPublicView ? '100%' : { xs: '100%', lg: 'calc(100% - 80px)' };
    const contentLeft  = isPublicView ? 0      : { xs: '0px', lg: '80px' };
    const contentTop   = isPublicView ? '56px' : 8;

    if (isLoading) {
        return (
            <>
                {isPublicView ? <PublicReportHeader /> : <DashboardHeaderSection hideSearch />}
                <Box width={contentWidth} marginLeft={contentLeft} marginTop={contentTop}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', bgcolor: '#F0F2F5' }}>
                    <CircularProgress size={48} thickness={4} sx={{ color: C.orange }} />
                </Box>
            </>
        );
    }

    if (isError || !summary) {
        return (
            <>
                {isPublicView ? <PublicReportHeader /> : <DashboardHeaderSection hideSearch />}
                <Box width={contentWidth} marginLeft={contentLeft} marginTop={contentTop} sx={{ p: 3, bgcolor: '#F0F2F5', minHeight: '100vh' }}>
                    {/* "Back to Reports" only makes sense for a logged-in visitor —
                        /reports is auth-gated, so a public share-link viewer would
                        just hit a login wall clicking it. */}
                    <Alert severity="error" action={!isPublicView && (
                        <Button color="inherit" size="small" onClick={() => navigate('/reports')}>Back to Reports</Button>
                    )}>
                        Failed to load this report group. It may have been deleted.
                    </Alert>
                </Box>
            </>
        );
    }

    return (
        <>
            {isPublicView ? <PublicReportHeader campaignName={summary.group?.name} /> : <DashboardHeaderSection hideSearch />}
            <Box
                width={contentWidth}
                marginLeft={contentLeft}
                marginTop={contentTop}
                sx={{ bgcolor: '#F0F2F5', minHeight: '100vh', p: { xs: 1.5, sm: 2, md: 3 } }}
            >
                {/* ── Header ─────────────────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 42, height: 42, borderRadius: '12px',
                            background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            boxShadow: '0 4px 12px rgba(255,82,0,0.3)',
                        }}>
                            <FolderOpenOutlined sx={{ color: '#fff', fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                                {summary.group?.name}
                            </Typography>
                            {/* group.brand comes pre-extracted from the backend as a plain
                                name string (see routes/reportGroups.js's `group.brand?.name`),
                                not an object like a report's `brand` field. */}
                            {summary.group?.brand && (
                                <Typography sx={{ fontSize: 12.5, color: '#9CA3AF', mt: 0.3 }}>
                                    {summary.group.brand}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {!isPublicView && (
                        <Button
                            variant="contained"
                            startIcon={<AddCircleOutline sx={{ fontSize: 17 }} />}
                            onClick={handleAddReport}
                            sx={{
                                fontSize: '13.5px', textTransform: 'none', fontWeight: 600,
                                background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                                borderRadius: '24px', px: 3, py: 1,
                                boxShadow: '0 4px 14px rgba(255,82,0,0.3)',
                                '&:hover': { background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)', opacity: 0.9 },
                            }}
                        >
                            Add Report
                        </Button>
                    )}
                </Box>

                {!hasReports ? (
                    <Box sx={{
                        textAlign: 'center', py: 8, bgcolor: '#fff', borderRadius: '16px',
                        border: `1px solid ${C.border}`, boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                    }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827', mb: 0.5 }}>
                            No reports in this group yet
                        </Typography>
                        {isPublicView ? (
                            <Typography sx={{ fontSize: 13, color: '#9CA3AF' }}>
                                Check back once reports have been added to this group.
                            </Typography>
                        ) : (
                            <>
                                <Typography sx={{ fontSize: 13, color: '#9CA3AF', mb: 2.5 }}>
                                    Add an existing report or create a new one directly inside this group.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddCircleOutline sx={{ fontSize: 17 }} />}
                                    onClick={handleAddReport}
                                    sx={{
                                        fontSize: '13.5px', textTransform: 'none', fontWeight: 600,
                                        background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                                        borderRadius: '24px', px: 3, py: 1,
                                        boxShadow: '0 4px 14px rgba(255,82,0,0.3)',
                                    }}
                                >
                                    Add Report
                                </Button>
                            </>
                        )}
                    </Box>
                ) : (
                    <>
                        <GroupTabs tabs={tabs} activeId={tab} onChange={setTab} onRemoveTab={handleRemoveTab} readOnly={isPublicView} />

                        {tab === 'overview' ? (
                            <>
                                <OverviewSubTabs activeId={overviewSubTab} onChange={setOverviewSubTab} />
                                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress size={40} sx={{ color: C.orange }} /></Box>}>
                                    {overviewSubTab === 'dashboard' && (
                                        <DashboardTab
                                            report={overviewReport}
                                            stats={overviewStats}
                                            snapshots={groupSnapshots}
                                            onNavigateTab={() => {}}
                                            isPublic
                                        />
                                    )}
                                    {overviewSubTab === 'influencers' && (
                                        <InfluencersTab
                                            influencers={mergedInfluencersMapped}
                                            isPublic
                                            platform={null}
                                        />
                                    )}
                                    {overviewSubTab === 'contents' && (
                                        <ContentsTab report={mergedPostsReport} isPublic />
                                    )}
                                </Suspense>
                            </>
                        ) : (
                            // Reuse the full single-report dashboard for a child-report tab —
                            // `embedded` suppresses its own header/sidebar chrome (this page
                            // already provides both), and `reportIdOverride` points it at the
                            // selected child report instead of reading useParams().
                            <CampaignReportDashboard reportIdOverride={tab} embedded />
                        )}
                    </>
                )}
            </Box>

            {/* ── Remove Report From Group Confirmation Dialog ────────────────────── */}
            <Dialog
                open={Boolean(pendingDetach)}
                onClose={handleCancelDetach}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    elevation: 0,
                    sx: { borderRadius: '20px', overflow: 'hidden', border: '1px solid #E5E7EB', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' },
                }}
            >
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #EF4444 0%, #F87171 100%)', flexShrink: 0 }} />
                <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: 3, pb: { xs: 2, sm: 3.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '11px',
                            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <WarningAmberRounded sx={{ color: '#EF4444', fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
                                Remove Report From Group?
                            </Typography>
                            <Typography sx={{ color: '#9CA3AF', fontSize: { xs: 10, sm: 12 }, mt: 0.3 }}>
                                The report itself is kept — only unlinked
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', px: 2, py: 1.4, mb: 2.5 }}>
                        <Typography sx={{ color: '#6B7280', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.4 }}>
                            Report
                        </Typography>
                        <Typography sx={{ color: '#111827', fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {pendingDetach?.label}
                        </Typography>
                    </Box>

                    <Typography sx={{ color: '#6B7280', fontSize: 13, lineHeight: 1.6, mb: detachError ? 1.5 : 3 }}>
                        This won't delete the report or any of its data — it'll just leave "{summary.group?.name}" and show up in your normal Reports list again, exactly as it was before.
                    </Typography>

                    {detachError && (
                        <Box sx={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', px: 2, py: 1, mb: 2 }}>
                            <Typography sx={{ color: '#EF4444', fontSize: 12.5 }}>{detachError}</Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            fullWidth
                            disabled={detachMutation.isPending}
                            onClick={handleCancelDetach}
                            sx={{
                                color: '#6B7280', background: '#fff', borderRadius: '10px', py: 1.1,
                                fontSize: 13.5, fontWeight: 500, textTransform: 'none',
                                border: '1px solid #E5E7EB',
                                '&:hover': { background: '#F9FAFB', borderColor: '#D1D5DB' },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={detachMutation.isPending}
                            onClick={handleConfirmDetach}
                            sx={{
                                background: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)',
                                borderRadius: '10px', py: 1.1, fontSize: 13.5, fontWeight: 600, textTransform: 'none',
                                boxShadow: '0 4px 14px rgba(239,68,68,0.3)',
                                '&:hover': { background: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)', opacity: 0.9, boxShadow: '0 6px 20px rgba(239,68,68,0.4)' },
                                '&.Mui-disabled': { background: '#F3F4F6', color: '#9CA3AF', boxShadow: 'none' },
                            }}
                        >
                            {detachMutation.isPending ? 'Removing…' : 'Yes, Remove'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            <CreateReportDialog
                open={addReportOpen}
                onClose={() => setAddReportOpen(false)}
                groupId={groupId}
                groupName={summary.group?.name}
                onCreated={handleReportCreated}
                onSuccess={(message) => { setSuccessMessage(message); setOpenAlert(true); }}
            />

            <Snackbar
                open={openAlert}
                autoHideDuration={8000}
                onClose={() => setOpenAlert(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenAlert(false)} severity="success" sx={{ width: '100%' }}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ReportGroupDashboard;
